import type { components } from "../api/schema";
import { unicodeCaseFold } from "../lib/unicodeCaseFold";
import {
  cascadeFlags,
  declarationConflict,
  declarationConflictMessage,
  groupDeclarationsByKey,
  type DeclarationConflict,
} from "../lib/variableCascade";
import { parseVariableValue } from "./variables/variableFormState";

type JsonValue = components["schemas"]["JsonValue"];
export type ScenarioRow = components["schemas"]["ScenarioRowResponse"];
export type Variable = components["schemas"]["VariableResponse"];

export interface DeclaredVariable {
  conflict: DeclarationConflict;
  declarations: Variable[];
  isSensitive: boolean;
  variable: Variable;
}

export type DraftCell =
  | { kind: "null" }
  | { kind: "unset" }
  | {
      kind: "value";
      raw?: Exclude<JsonValue, null>;
      text: string;
    };

export interface DraftRow {
  id: string;
  value: Record<string, DraftCell>;
}

export interface ImportedCsvColumn {
  header: string;
  id: string;
  index: number;
  label: string;
  isUnnamed: boolean;
}

export interface ImportedCsv {
  columns: ImportedCsvColumn[];
  rows: string[][];
}

export type CsvMappingTarget =
  | { kind: "ignore" }
  | { kind: "variable"; variableId: string };

export type CsvMapping = Record<string, CsvMappingTarget>;

export function normalizedVariableKey(key: string): string {
  return unicodeCaseFold(key);
}

function draftText(value: Exclude<JsonValue, null>): string {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

export function draftFromRow(row: ScenarioRow): DraftRow {
  return {
    id: row.id,
    value: Object.fromEntries(
      Object.entries(row.value ?? {}).map(([key, value]) => [
        key,
        value === null
          ? ({ kind: "null" } satisfies DraftCell)
          : ({
              kind: "value",
              raw: value,
              text: draftText(value),
            } satisfies DraftCell),
      ]),
    ),
  };
}

export function draftCellForVariable(
  value: Record<string, DraftCell>,
  variableKey: string,
): DraftCell | undefined {
  const normalizedKey = normalizedVariableKey(variableKey);
  return Object.entries(value).find(
    ([key]) => normalizedVariableKey(key) === normalizedKey,
  )?.[1];
}

export function withCanonicalDraftCell(
  value: Record<string, DraftCell>,
  variableKey: string,
  cell: DraftCell,
): Record<string, DraftCell> {
  const normalizedKey = normalizedVariableKey(variableKey);
  const canonicalEntries = Object.entries(value).filter(
    ([key]) => normalizedVariableKey(key) !== normalizedKey,
  );
  return Object.fromEntries(
    cell.kind === "unset"
      ? canonicalEntries
      : [...canonicalEntries, [variableKey, cell]],
  );
}

// Agent declarations are the ambient floor. Declarations owned by this
// scenario override case-insensitively and provide the canonical output key.
export function mergeDeclaredVariables(
  agentVariables: Variable[],
  scenarioVariables: Variable[],
  allVariables: Variable[],
): DeclaredVariable[] {
  const variablesByKey = new Map<string, Variable>();
  for (const variable of agentVariables) {
    variablesByKey.set(normalizedVariableKey(variable.key), variable);
  }
  for (const variable of scenarioVariables) {
    variablesByKey.set(normalizedVariableKey(variable.key), variable);
  }
  const groups = groupDeclarationsByKey(allVariables);
  return [...variablesByKey.entries()].map(([key, variable]) => {
    const declarations = groups.get(key) ?? [variable];
    const { isSensitive } = cascadeFlags(declarations);
    return {
      conflict: declarationConflict(declarations),
      declarations,
      isSensitive,
      variable,
    };
  });
}

export function declaredVariableErrors(
  variables: DeclaredVariable[],
): string[] {
  return variables.flatMap((entry) =>
    entry.conflict === "none"
      ? []
      : [
          // Cross-declaration value_type / allowed_values precedence is
          // unsettled. A row cannot safely choose a rendering declaration.
          declarationConflictMessage(
            entry.variable.key,
            entry.declarations.length,
            entry.conflict,
          ),
        ],
  );
}

export function undeclaredKeys(
  rows: DraftRow[],
  variables: Variable[],
): string[] {
  const declared = new Set(
    variables.map((variable) => normalizedVariableKey(variable.key)),
  );
  return [
    ...new Set(
      rows.flatMap((row) =>
        Object.keys(row.value).filter(
          (key) => !declared.has(normalizedVariableKey(key)),
        ),
      ),
    ),
  ];
}

export function enumValues(variable: Variable): string[] {
  const allowedValues = variable.current_revision.allowed_values;
  if (
    !Array.isArray(allowedValues) ||
    !allowedValues.every(
      (allowedValue): allowedValue is string =>
        typeof allowedValue === "string",
    )
  ) {
    throw new Error(
      `${variable.key} enum options are unavailable, so rows cannot be saved safely.`,
    );
  }
  return allowedValues;
}

function validateLoadedValue(
  value: Exclude<JsonValue, null>,
  variable: Variable,
): JsonValue {
  const valueType = variable.current_revision.value_type;
  switch (valueType) {
    case "bool":
      if (typeof value === "boolean") return value;
      break;
    case "number":
      if (typeof value === "number" && Number.isFinite(value)) {
        if (Number.isInteger(value) && !Number.isSafeInteger(value)) {
          throw new Error(`${variable.key} must be a safe integer.`);
        }
        return value;
      }
      break;
    case "string":
      if (typeof value === "string") return value;
      break;
    case "enum": {
      const allowedValues = enumValues(variable);
      if (typeof value !== "string") break;
      if (!allowedValues.includes(value)) {
        throw new Error(
          `${variable.key} must be one of: ${allowedValues.join(", ")}.`,
        );
      }
      return value;
    }
    default:
      throw new Error(
        `${variable.key} uses unsupported value type "${valueType}" and cannot be saved safely.`,
      );
  }
  throw new Error(
    `${variable.key} has a stored value that does not match type "${valueType}".`,
  );
}

function asTypedValue(
  cell: Extract<DraftCell, { kind: "value" }>,
  variable: Variable,
): JsonValue {
  if (cell.raw !== undefined) {
    return validateLoadedValue(cell.raw, variable);
  }
  // The empty string is an explicit string value. Only `unset` omits a key, so
  // a no-op save never reactivates a cascade default. Every other type rejects
  // a blank cell, and an unknown value type fails closed.
  return parseVariableValue(
    variable.key,
    variable.current_revision.value_type,
    variable.current_revision.value_type === "enum"
      ? enumValues(variable)
      : null,
    cell.text,
  );
}

export function draftCellError(
  cell: DraftCell,
  variable: Variable,
): string | null {
  if (cell.kind !== "value") return null;
  try {
    asTypedValue(cell, variable);
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : String(error);
  }
}

export function replacementRows(rows: DraftRow[], variables: Variable[]) {
  const variablesByKey = new Map(
    variables.map((variable) => [
      normalizedVariableKey(variable.key),
      variable,
    ]),
  );
  const validationErrors: string[] = [];
  const replacements = rows.map((row, index) => {
    const valueEntries: Array<[string, JsonValue]> = [];
    const normalizedKeys = new Set<string>();
    for (const [key, cell] of Object.entries(row.value)) {
      const normalizedKey = normalizedVariableKey(key);
      const variable = variablesByKey.get(normalizedKey);
      if (!variable) {
        continue;
      }
      if (normalizedKeys.has(normalizedKey)) {
        validationErrors.push(
          `Row ${String(index + 1)}: duplicate variable aliases for "${key}" must be resolved before saving.`,
        );
        continue;
      }
      normalizedKeys.add(normalizedKey);
      if (cell.kind === "unset") {
        continue;
      }
      if (cell.kind === "null") {
        valueEntries.push([variable.key, null]);
        continue;
      }
      try {
        valueEntries.push([variable.key, asTypedValue(cell, variable)]);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        validationErrors.push(`Row ${String(index + 1)}: ${message}`);
      }
    }
    return { sort_order: index, value: Object.fromEntries(valueEntries) };
  });
  if (validationErrors.length > 0) {
    throw new Error(validationErrors.join(" "));
  }
  return replacements;
}

export function duplicateMappingTargets(mapping: CsvMapping): Set<string> {
  const counts = new Map<string, number>();
  for (const target of Object.values(mapping)) {
    if (target.kind === "ignore") {
      continue;
    }
    counts.set(target.variableId, (counts.get(target.variableId) ?? 0) + 1);
  }
  return new Set(
    [...counts.entries()]
      .filter(([, count]) => count > 1)
      .map(([variableId]) => variableId),
  );
}

export function importedDraftRows(
  csv: ImportedCsv,
  mapping: CsvMapping,
  variables: Variable[],
): DraftRow[] {
  const duplicates = duplicateMappingTargets(mapping);
  if (duplicates.size > 0) {
    throw new Error("Each variable can be mapped from only one CSV column.");
  }
  const variablesById = new Map(
    variables.map((variable) => [variable.id, variable]),
  );
  const columnsById = new Map(csv.columns.map((column) => [column.id, column]));
  return csv.rows.map((source, index) => ({
    id: `csv-${String(index)}`,
    value: Object.fromEntries(
      Object.entries(mapping)
        .filter(
          (
            entry,
          ): entry is [
            string,
            Extract<CsvMappingTarget, { kind: "variable" }>,
          ] => entry[1].kind === "variable",
        )
        .map(([columnId, target]) => {
          const variable = variablesById.get(target.variableId);
          if (!variable) {
            throw new Error("A mapped variable is no longer available.");
          }
          const column = columnsById.get(columnId);
          if (!column) {
            throw new Error("A mapped CSV column is no longer available.");
          }
          return [
            variable.key,
            {
              kind: "value",
              text: source[column.index] ?? "",
            } satisfies DraftCell,
          ];
        }),
    ),
  }));
}
